import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createUser,
  getUsers,
  getUserById,
  updateUser,
  deleteUser,
  updatePassword,
  createUserPermission,
  updateUserPermission,
  deleteUserPermission,
  fetchUserPermissionsByUserId,
} from './userAction';

// Types for User, Permissions and State
interface User {
  UserId: number;
  Username: string;
  DisplayName: string;
  Email: string;
  Source: string;
  PasswordHash: string;
  PasswordSalt: string;
  CreatedAt: string;
  ModifiedAt: string;
  IsActive: number;
  owner_id: number;
  role_id: number;
  fy_id: number;
  login_otp_authenticate_id: number;
  image: string;
  mobile_flags: string;
  mobile_code: string;
  enc_pass: string;
  active_deactive: string;
  path: string;
  fyName: string;
}

interface Permission {
  permissionKey: string;
  granted: boolean;
}

interface UserState {
  users: User[];
  totalUsers: any[];
  userPermissions: any[];
  loading: boolean;
  error: string | null;
  passwordUpdateStatus: string | null; // Added state to handle password update status
}

const initialState: UserState = {
  users: [],
  totalUsers: [],
  userPermissions: [],
  loading: false,
  error: null,
  passwordUpdateStatus: null, // Initialize password update status as null
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.users.push(action.payload.data); // Add the new user to the users array
      })
      .addCase(createUser.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUsers.fulfilled, (state, action: PayloadAction<{ data: User[] }>) => {
        state.loading = false;
        state.totalUsers = action.payload.data;
      })
      .addCase(getUsers.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getUserById.fulfilled, (state, action: PayloadAction<User>) => {
        state.users = [action.payload]; // Set the single user as the active user
      })
      
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<any>) => {
        state.users = state.users.map(user => user.UserId === action.payload.UserId ? action.payload : user);
      })
      
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<number>) => {
        state.users = state.users.filter(user => user.UserId !== action.payload);
      })
      
      // Handling Password Update
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
        state.passwordUpdateStatus = null; 
      })
      .addCase(updatePassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.passwordUpdateStatus = 'Password updated successfully'; 
      })
      .addCase(updatePassword.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.passwordUpdateStatus = action.payload || 'Error updating password';
      })

      // User Permissions: Create
      .addCase(createUserPermission.pending, (state) => {
        state.loading = true;
      })
      .addCase(createUserPermission.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userPermissions.push(action.payload); // Add the new user permission
      })
      .addCase(createUserPermission.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // User Permissions: Update
      .addCase(updateUserPermission.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserPermission.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userPermissions = state.userPermissions.map((permission) =>
          permission.userPermissionId === action.payload.userPermissionId
            ? action.payload
            : permission
        );
      })
      .addCase(updateUserPermission.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // User Permissions: Delete
      .addCase(deleteUserPermission.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserPermission.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userPermissions = state.userPermissions.filter(
          (permission) => permission.userPermissionId !== action.payload.userPermissionId
        );
      })
      .addCase(deleteUserPermission.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch User Permissions
      .addCase(fetchUserPermissionsByUserId.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserPermissionsByUserId.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.userPermissions = action.payload.data; // Add fetched user permissions to the state
      })
      .addCase(fetchUserPermissionsByUserId.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default userSlice.reducer;
